import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button, Dropdown } from '@praetorian-chariot/ui';
import { PropsWithChildren } from 'react';

import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { exportFormatOptions } from '@/sections/export/constants';
import type { DropdownOption } from '@/types/dropdown.types';
import { pluralize } from '@/utils/pluralize.util';

type ExportModalProps = PropsWithChildren & {
  open: boolean;
  onClose: () => void;
  type: string;
  selected?: {
    selectedRows: string[];
    onClearSelectedRows: () => void;
  };
  onExport: () => void;
  exportFormat: DropdownOption;
  onChangeExportFormat: (option: DropdownOption) => void;
  disabled?: boolean;
  loading?: boolean;
};

export function ExportModal({
  open,
  onClose,
  type,
  children,
  selected,
  onExport,
  exportFormat,
  onChangeExportFormat,
  disabled,
  loading,
}: ExportModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      hideClose
      footer={{
        text: 'Export',
        disabled,
        onClick: onExport,
        // left: (
        //   <Button
        //     className="border-0 p-0"
        //     label="Set as Default Setting"
        //     styleType="tertiary"
        //     onClick={onClose}
        //   />
        // ),
      }}
      title={<h2 className="py-2 text-lg font-bold">{type} Export Options</h2>}
      className="scrollbar-dark border-chariot-stroke border-2 border-x-0 p-0"
    >
      {selected && selected.selectedRows.length > 0 && (
        <div className="border-chariot-stroke flex items-center justify-between border-b-2 px-6 py-4">
          <p>
            Export limited to {selected.selectedRows.length} selected{' '}
            {pluralize(selected.selectedRows.length, type).toLocaleLowerCase()}
          </p>
          <Button
            startIcon={<XMarkIcon className="size-4" />}
            label="Clear"
            size="xs"
            styleType="rounded"
            onClick={selected.onClearSelectedRows}
          />
        </div>
      )}

      <div className="scrollbar-dark max-h-[550px] overflow-auto px-3 py-4">
        {loading && (
          <>
            <Loader isLoading={true} className="mb-4 h-[200px] w-full" />
            <Loader isLoading={true} className="mb-4 h-[200px] w-full" />
            <Loader isLoading={true} className="h-[200px] w-full" />
          </>
        )}
        {!loading && <>{children}</>}
      </div>

      <div
        data-testid="export-format"
        className="border-chariot-stroke flex items-center justify-between border-t-2 px-6 py-4"
      >
        <p>Export Format</p>
        <Dropdown
          options={exportFormatOptions}
          size="sm"
          onSelect={onChangeExportFormat}
          selected={exportFormat ? [exportFormat] : []}
          listClassName="w-fit"
        />
      </div>
    </Modal>
  );
}
